import { Component, HostBinding, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { ApContentModule, ApContentService } from '@ap/content';
import { ApSearchInputComponent } from '@ap/input/search';
import { ApModalService, ModalClosedReason, ModalContent, ModalOptions } from '@ap/modal';
import { ApNotificationComponent } from '@ap/notification';

import { ApSelectorOption, ApSelectorSelectionType } from '../selector.modal';

import { ApSelectorModalGroup, ApSelectorModalOptions } from './selector-modal.model';
import { ApSelectorModalSingleComponent } from './single/selector-modal-single.component';
import { ApSelectorModalMultipleComponent } from './multiple/selector-modal-multiple.component';
import { ApSelectorSubModalOptions } from './sub-modal/selector-sub-modal.model';
import { ApSelectorSubModalComponent } from './sub-modal/selector-sub-modal.component';
import content from './selector-modal.content.json';

@Component({
    selector: 'ap-selector-modal',
    templateUrl: './selector-modal.component.html',
    styleUrl: './selector-modal.component.scss',
    encapsulation: ViewEncapsulation.Emulated,
    imports: [
        ApSelectorModalSingleComponent,
        ApSelectorModalMultipleComponent,
        ApContentModule,
        ApSearchInputComponent,
        ApNotificationComponent,
        ReactiveFormsModule
    ]
})
export class ApSelectorModalComponent extends ModalContent<ApSelectorModalOptions> implements OnInit, OnDestroy {
    private readonly contentService = inject(ApContentService);
    private readonly modalService = inject(ApModalService);

    @HostBinding('attr.data-selection-type') protected selectionType: ApSelectorSelectionType = 'single';

    protected searchField = new FormControl('');
    protected selectedOptions: ApSelectorOption[];

    /**
     * All options for the selector
     */
    protected options: ApSelectorOption[];

    /**
     * Options filtered by search value
     */
    protected filteredOptions: ApSelectorOption[];

    /**
     * Options limited by `maxVisibleOptions`
     */
    protected optionsToDisplay: ApSelectorOption[];

    /**
     * Maximum amount of options to show.
     * If more `filteredOptions` a notification will be shown
     */
    protected maxVisibleOptions = 10;

    /**
     * Optional grouping of items
     */
    protected groups: ApSelectorModalGroup[];

    /**
     * Title of the modal
     */
    protected title: string;

    private readonly destroy$ = new Subject<void>();

    constructor() {
        super();
        this.contentService.addContentItems(content);
    }

    /**
     * On init of modal set options and watch search field change
     */
    public ngOnInit(): void {
        this.title = this.modalOptions?.title;
        this.options = this.modalOptions?.options?.options || [];
        this.selectedOptions = this.modalOptions?.options?.selectedOptions || [];
        this.selectionType = this.modalOptions?.options?.selectionType || 'single';
        this.maxVisibleOptions = this.modalOptions?.options?.maxVisibleOptions || 10;
        this.groups = this.modalOptions?.options?.groups;
        this.maxVisibleOptions = this.modalOptions?.options?.maxVisibleOptions || 10;
        this.filteredOptions = this.options;
        this.setOptionsToDisplay();
        this.searchField.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string) => {
            this.handleSearchChange(value);
        });
    }

    /**
     * Unsubscribe on angular lifecycle call
     */
    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Select an option and close modal
     */
    protected select(option: ApSelectorOption | ApSelectorOption[]): void {
        if (Array.isArray(option)) {
            this.close(ModalClosedReason.SELECTED, option);
            return;
        }

        this.close(ModalClosedReason.SELECTED, [option]);
    }

    /**
     * Open sub selector modal to select sub options
     */
    public openSubModal(option: ApSelectorOption): void {
        const modalOptions = new ModalOptions<ApSelectorSubModalOptions>({
            option
        });
        modalOptions.isSubModal = true;
        modalOptions.title = option.labels?.modalTitle || '';

        const spaarpotSelectorRef = this.modalService.openSubModal(() => this.cancel(), ApSelectorSubModalComponent, modalOptions);
        spaarpotSelectorRef.afterClosed().subscribe((result) => {
            if (result.reason === ModalClosedReason.SELECTED) {
                this.select(result.data);
            }
        });
    }

    /**
     * When input from search field changes, filter all options and options in sub arrays
     */
    private handleSearchChange(value: string): void {
        if (!value) {
            this.filteredOptions = this.options;
            this.setOptionsToDisplay();
            return;
        }

        this.filteredOptions = this.options.filter(
            (option) =>
                option.title?.toLowerCase().includes(value.toLowerCase()) ||
                option.text?.toLowerCase().includes(value.toLowerCase()) ||
                option.subtitle?.toLowerCase().includes(value.toLowerCase())
        );
        this.setOptionsToDisplay();
    }

    /**
     * Set to max visible items
     */
    private setOptionsToDisplay(): void {
        this.optionsToDisplay = this.filteredOptions.slice(0, this.maxVisibleOptions);
    }
}
